<template>
    <div class="main-layout__container auction-edit">
        <div class="content__header content__header--one-item-per-line">
            <div class="content-header__back">
                <UIBackButton
                    :text="'К списку аукционов'"
                    :to="router.resolve({ name: 'AuctionsList' }).href"
                />
            </div>

            <div class="content-header__title">Редактирование лота №{{ auctionId }}</div>

            <div class="content-header__background content-header__background--add-edit-page"></div>
        </div>

        <UILoader v-if="isAuctionLoading && !auction" class="editable" />

        <template v-else-if="!isAuctionLoading && auction">
            <div class="content__body">
                <div class="form__content">
                    <div class="form__fields">
                        <div class="form__field-group">
                            <UIDatePicker
                                v-model="form.start_date"
                                label="Дата начала"
                                :options="{ enableTime: true }"
                                :error-message="errors?.start_date?.join() ?? ''"
                            />

                            <UIDatePicker
                                v-model="form.end_date"
                                label="Дата окончания"
                                :options="{ enableTime: true }"
                                :error-message="errors?.end_date?.join() ?? ''"
                            />

                            <UIInput
                                id="currency_input"
                                v-model="formStartPrice"
                                label="Стартовая цена"
                                :is-currency="true"
                                :error-message="errors?.start_price?.join() ?? ''"
                            />
                        </div>
                    </div>
                </div>

                <div class="auction-edit__content">
                    <div class="auction-edit-car">
                        <div class="auction-edit-car-info">
                            <div class="auction-edit-car-info__title">
                                {{ getCarFullName(auction.car) }}
                            </div>

                            <div v-if="auction.car.tags.length > 0" class="auction-edit-car-info__tags">
                                <CarTags :tags="auction.car.tags" />
                            </div>

                            <div class="auction-edit-car-info__specs">
                                {{ getCarSpecification(auction.car) }}
                            </div>

                            <div v-if="auction.car.comment" class="auction-edit-car-info__description">
                                {{ auction.car.comment }}
                            </div>
                        </div>

                        <AuctionCarGallery :auction="auction" />

                        <CarItemDetails
                            :details="[
                                { title: 'Тип поступления:', value: getCarAdmissionTypeLabel(auction.car.admission_type) },
                                { title: 'Тип НДС:', value: getCarNdsTypeLabel(auction.car.nds_type) },
                                { title: 'Комплектация:', value: auction.car.complectation.code },
                                { title: 'VIN:', value: auction.car.vin },
                                { title: 'ПТС:', value: auction.car.pts_number },
                                { title: 'Добавлен:', value: formatFromUTC(auction.car.created_at) },
                                { title: 'Дилер:', value: getCarDealerFullName(auction.car) },
                            ]"
                        />

                        <UIAccordionList :items="characteristics">
                            <template #content-0>
                                <CarCharacteristics :car="auction.car" />
                            </template>

                            <template #content-1>
                                <CarEquipment :car="auction.car" />
                            </template>

                            <template #content-2>
                                <CarDamage :car="auction.car" />
                            </template>

                            <template #content-3>
                                <CarLegal :car="auction.car" />
                            </template>

                            <template #content-4>
                                <div class="auction-edit-car__info-documents">
                                    <CarDocuments
                                        type-title="Диагностическая карта"
                                        :car-documents="carDocuments.diagnostic_card"
                                        if-no-documents-show-this="file-not-uploaded"
                                    />

                                    <CarDocuments
                                        type-title="Отчет автокод/автотека"
                                        :car-documents="carDocuments.avtocod_report"
                                        if-no-documents-show-this="file-not-uploaded"
                                    />

                                    <CarDocuments
                                        type-title="Дополнительные документы"
                                        :car-documents="carDocuments.document"
                                    />
                                </div>
                            </template>
                        </UIAccordionList>
                    </div>
                </div>
            </div>

            <div class="content__footer">
                <div class="form__actions form__actions--2-blocks">
                    <div>
                        <UISimpleButton
                            button-type="danger"
                            @clicked="onDeleteAuction"
                        >
                            <template #icon>
                                <img v-svg-inline src="@/assets/icons/ui/trash.svg" />
                            </template>
                            Удалить лот
                        </UISimpleButton>
                    </div>

                    <div class="form__actions">
                        <UISimpleButton
                            :button-type="auction.status === AuctionStatus.Draft ? 'outlined' : 'filled'"
                            :loading="saveLoading"
                            @clicked="onSaveAuction"
                        >
                            Сохранить
                        </UISimpleButton>

                        <UISimpleButton
                            v-if="auction.status === AuctionStatus.Draft"
                            @clicked="onPublishAuction"
                        >
                            Опубликовать
                        </UISimpleButton>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import { computed, h, inject, onMounted, ref, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';

    import { Auction, AuctionStatus, AuctionUpdateForm, AuctionUpdateFormErrors } from '@/types/auction.type.ts';
    import {
        CarDocument,
        CarDocumentType,
        getCarAdmissionTypeLabel,
        getCarNdsTypeLabel,
    } from '@/types/car.type.ts';

    import useAuctionRepository from '@/repositories/auction/useAuctionRepository.ts';
    import useLoading from '@/utils/useLoading.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import useUnsavedDataWarning from '@/plugins/useUnsavedDataWarning.ts';

    import { getCarDealerFullName, getCarDocuments, getCarFullName, getCarSpecification } from '@/utils/car';
    import { formatFromUTC, formatToUTC } from '@/utils/DateTimeFormatter.ts';
    import { ModalPlugin } from '@/plugins/modalPlugin.ts';

    import UIAccordionList from '@/components/ui/UIAccordionList/UIAccordionList.vue';
    import UIBackButton from '@/components/ui/UIBackButton/UIBackButton.vue';
    import UIDatePicker from '@/components/ui/UIDatePicker/UIDatePicker.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import AuctionCarGallery from '@/components/auction/AuctionCarGallery/AuctionCarGallery.vue';
    import AuctionConfirmDelete from '@/components/auction/AuctionConfirmDelete/AuctionConfirmDelete.vue';
    import AuctionConfirmPublish from '@/components/auction/AuctionConfirmPublish/AuctionConfirmPublish.vue';
    import CarCharacteristics from '@/components/cars/CarCharacteristics/CarCharacteristics.vue';
    import CarEquipment from '@/components/cars/CarEquipment/CarEquipment.vue';
    import CarDamage from '@/components/cars/CarDamage/CarDamage.vue';
    import CarDocuments from '@/components/cars/CarDocuments/CarDocuments.vue';
    import CarItemDetails from '@/components/cars/CarItemDetails/CarItemDetails.vue';
    import CarLegal from '@/components/cars/CarLegal/CarLegal.vue';
    import CarTags from '@/components/cars/CarTags/CarTags.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';

    const $modal = inject('$modal') as typeof ModalPlugin;

    const router = useRouter();
    const route = useRoute();
    const { getAuction, updateAuction, publishAuction, deleteAuction } = useAuctionRepository();
    const { showSuccess } = useNotifications();
    const { setDirty } = useUnsavedDataWarning();

    const auction = ref<Auction | null>(null);
    const form = ref<AuctionUpdateForm>({
        start_date: '',
        end_date: '',
        start_price: 0,
    });
    const errors = ref<AuctionUpdateFormErrors | null>(null);
    const { isLoading: isAuctionLoading, setLoading: setAuctionLoading } = useLoading();
    const saveLoading = ref<boolean>(false);
    const characteristics = ref([
        { title: 'Характеристики', content: '' },
        { title: 'Комплектация', content: '' },
        { title: 'Диагностика и повреждения', content: '' },
        { title: 'Юридическая чистота', content: '' },
        { title: 'Документы', content: '' },
    ]);

    const formStartPrice = computed<string>({
        get: () => `${form.value.start_price}`,
        set: (price: string) => {
            form.value.start_price = price.match(/^\d+$/) ? parseInt(price) : 0;
        },
    });

    const carDocuments = computed<Record<CarDocumentType, CarDocument[]>>(() => {
        return getCarDocuments(auction.value?.car);
    });

    watch(form, () => {
        setDirty(true);
    }, { deep: true });

    const auctionId = route.params.auction_id as unknown as number;

    function onSaveAuction(): void {
        saveLoading.value = true;

        saveAuction()
            .then(() => {
                showSuccess({
                    title: 'Изменения в аукционе сохранены',
                });

                router.push({ name: 'AuctionsList' });
            })
            .finally(() => {
                saveLoading.value = false;
            });
    }

    function onPublishAuction(): void {
        const requestForm = mapAuctionUpdateFormToRequestForm();

        $modal.open({
            component: AuctionConfirmPublish,
            modalType: 'narrow',
            props: {
                startDate: requestForm.start_date,
                endDate: requestForm.end_date,
                cars: auction.value ? [auction.value.car] : [],
                onConfirm: async () => {
                    // noinspection EmptyCatchBlockJS
                    try {
                        await saveAuction();
                        await publishAuction(auctionId);

                        showSuccess({
                            title: 'Аукцион опубликован',
                        });

                        router.push({ name: 'AuctionsList' });
                    }
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    catch (e) {}
                },
            },
        });
    }

    function onDeleteAuction(): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    auction.value ? h(AuctionConfirmDelete, { auction: auction.value }) : '',
            },
            props: {
                onConfirm: async () => {
                    await deleteAuction(auctionId);

                    showSuccess({
                        title: 'Аукцион удален',
                    });

                    router.push({ name: 'AuctionsList' });
                },
            },
        });
    }

    function saveAuction(): Promise<void> {
        errors.value = null;

        return new Promise((resolve, reject) => {
            updateAuction(auctionId, mapAuctionUpdateFormToRequestForm())
                .then(() => {
                    setDirty(false);
                    resolve();
                })
                .catch((updateErrors: AuctionUpdateFormErrors) => {
                    errors.value = updateErrors;
                    reject();
                });
        });
    }

    function mapAuctionUpdateFormToRequestForm(): AuctionUpdateForm {
        return {
            start_date: form.value.start_date.length > 0
                ? formatToUTC(form.value.start_date, { fromFormat: 'dd.LL.yyyy HH:mm' })
                : '',
            end_date: form.value.end_date.length > 0?
                formatToUTC(form.value.end_date, { fromFormat: 'dd.LL.yyyy HH:mm' })
                : '',
            start_price: form.value.start_price,
        };
    }

    function fetchAuction(): void {
        setAuctionLoading(true);

        getAuction(auctionId).then((fetchedAuction: Auction) => {
            auction.value = fetchedAuction;

            form.value = {
                start_date: formatFromUTC(fetchedAuction.start_date, { toFormat: 'dd.LL.yyyy HH:mm' }),
                end_date: formatFromUTC(fetchedAuction.end_date, { toFormat: 'dd.LL.yyyy HH:mm' }),
                start_price: fetchedAuction.start_price,
            };

            setAuctionLoading(false);
        });
    }

    onMounted(() => {
        fetchAuction();
    });
</script>

<style scoped lang="scss">
    @use './EditAuction.scss' as *;
</style>
